import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as MdIcons from 'react-icons/md';
import { exportToMP4 } from '../utils/videoExporter';

const { MdClose, MdOndemandVideo, MdImage, MdMusicNote, MdCheckCircle, MdDownload, MdWarning } = MdIcons;

const ImageUploader = ({ file, onFileSelect }) => {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreview(null);
    }
  }, [file]);

  return (
    <div 
      onClick={() => inputRef.current?.click()}
      className={`relative h-40 rounded-xl border-2 border-dashed cursor-pointer transition-all flex flex-col items-center justify-center overflow-hidden group ${
        file ? 'border-primary bg-primary/5' : 'border-slate-300 dark:border-border-dark hover:border-primary/50'
      }`}
    >
      <input 
        type="file" 
        accept="image/jpeg, image/png, image/webp" 
        className="hidden" 
        ref={inputRef}
        onChange={(e) => {
          if (e.target.files[0]) onFileSelect(e.target.files[0]);
        }}
      />
      
      {preview ? (
        <>
          <img src={preview} alt="Thumbnail preview" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-30 transition-opacity" />
          <div className="z-10 flex flex-col items-center gap-2 bg-black/40 backdrop-blur-sm p-3 rounded-xl text-white">
            <SafeIcon icon={MdCheckCircle} className="text-2xl text-green-400" />
            <span className="text-sm font-bold text-center px-4 truncate w-48">{file.name}</span>
          </div>
        </>
      ) : (
        <>
          <div className="w-12 h-12 rounded-full bg-slate-200 dark:bg-surface-dark text-slate-500 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <SafeIcon icon={MdImage} className="text-2xl" />
          </div>
          <span className="text-sm font-bold text-slate-700 dark:text-slate-300">Upload Cover Artwork</span>
          <span className="text-xs text-slate-500 mt-1">1920x1080 (16:9) recommended</span>
        </>
      )}
    </div>
  );
};

const VideoExportModal = ({ isOpen, onClose, audioFile }) => {
  const [imageFile, setImageFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressState, setProgressState] = useState({ phase: 'idle', progress: 0, text: '' });
  const [error, setError] = useState(null);
  const [resultUrl, setResultUrl] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setImageFile(null);
      setProgressState({ phase: 'idle', progress: 0, text: '' });
      setError(null);
      setResultUrl(null);
    }
  }, [isOpen]);

  const handleExport = async () => {
    if (!audioFile || !imageFile) return;
    
    setIsProcessing(true);
    setError(null);
    setProgressState({ phase: 'starting', progress: 0, text: 'Initializing...' });
    
    try {
      const mp4Blob = await exportToMP4(audioFile, imageFile, (status) => {
        setProgressState(status);
      });
      
      const url = URL.createObjectURL(mp4Blob);
      setResultUrl(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!resultUrl) return;
    const a = document.createElement('a');
    a.href = resultUrl;
    const audioName = audioFile.name.replace(/\.[^/.]+$/, "");
    a.download = `${audioName}_Video.mp4`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }} 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={!isProcessing ? onClose : undefined}
        />
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }} 
          animate={{ opacity: 1, scale: 1, y: 0 }} 
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-lg bg-white dark:bg-background-dark border border-slate-200 dark:border-border-dark rounded-2xl shadow-2xl overflow-hidden"
        >
          <div className="p-6 border-b border-slate-200 dark:border-border-dark flex justify-between items-center bg-slate-50 dark:bg-surface-dark/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center text-red-500">
                <SafeIcon icon={MdOndemandVideo} className="text-xl" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">Create Video</h3>
            </div>
            {!isProcessing && (
              <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-white/10 text-slate-500 dark:text-slate-400 transition-colors">
                <SafeIcon icon={MdClose} className="text-xl" />
              </button>
            )}
          </div>

          <div className="p-6 space-y-6">
            {!resultUrl ? (
              <>
                <p className="text-sm text-slate-500 dark:text-text-secondary leading-relaxed">
                  Combine your current audio track with a cover image to generate a YouTube-ready 1080p MP4 file. This happens entirely in your browser.
                </p>

                <div className="space-y-4">
                  {/* Selected Audio Display */}
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-surface-dark border border-slate-200 dark:border-border-dark">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary shrink-0">
                      <SafeIcon icon={MdMusicNote} />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs font-bold text-slate-500 uppercase">Audio Track</span>
                      <span className="text-sm font-medium text-slate-900 dark:text-white truncate">
                        {audioFile ? audioFile.name : 'No audio selected'}
                      </span>
                    </div>
                  </div>

                  {/* Image Uploader */}
                  <ImageUploader file={imageFile} onFileSelect={setImageFile} />
                </div>

                {error && (
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 text-sm font-medium border border-red-200 dark:border-red-500/20">
                    <div className="flex items-center gap-2 mb-1">
                      <SafeIcon icon={MdWarning} />
                      <strong>Export Failed</strong>
                    </div>
                    {error}
                  </div>
                )}

                {isProcessing && (
                  <div className="p-5 rounded-xl bg-slate-50 dark:bg-surface-dark border border-slate-200 dark:border-border-dark flex flex-col items-center gap-4">
                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300">{progressState.text}</p>
                    <div className="w-full h-2 bg-slate-200 dark:bg-border-dark rounded-full overflow-hidden relative">
                      {progressState.phase === 'loading' ? (
                        <motion.div className="absolute top-0 left-0 bottom-0 bg-red-500 w-1/3" animate={{ x: ['-100%', '300%'] }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }} />
                      ) : (
                        <div className="h-full bg-red-500 transition-all duration-300" style={{ width: `${progressState.progress}%` }} />
                      )}
                    </div>
                    <p className="text-xs text-slate-500 text-center">
                      Please keep this tab open. Video encoding in the browser may take a few minutes depending on track length.
                    </p>
                  </div>
                )}
              </>
            ) : (
              // Success State
              <div className="flex flex-col items-center justify-center py-6 text-center space-y-4">
                <div className="w-20 h-20 bg-green-100 dark:bg-green-500/20 rounded-full flex items-center justify-center mb-2">
                  <SafeIcon icon={MdCheckCircle} className="text-5xl text-green-500" />
                </div>
                <h4 className="text-2xl font-bold text-slate-900 dark:text-white">Video Ready!</h4>
                <p className="text-slate-500 dark:text-text-secondary">
                  Your MP4 has been successfully rendered and is ready for YouTube.
                </p>
                
                <button 
                  onClick={handleDownload}
                  className="mt-4 px-8 py-3.5 rounded-xl font-bold text-white bg-green-500 hover:bg-green-600 flex items-center justify-center gap-2 transition-all shadow-lg shadow-green-500/25 w-full"
                >
                  <SafeIcon icon={MdDownload} className="text-xl" />
                  Download MP4
                </button>
              </div>
            )}
          </div>

          {!resultUrl && (
            <div className="p-6 pt-0">
              <button 
                onClick={handleExport}
                disabled={!audioFile || !imageFile || isProcessing}
                className="w-full py-3.5 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-lg shadow-red-500/25"
              >
                {isProcessing ? 'Encoding...' : 'Generate MP4 Video'}
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default VideoExportModal;